import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { getThemeLabel } from '../../data/products';
import './ProductCard.css';

const STATUS_LABEL = {
  live: '서비스 중',
  beta: '베타',
  wip: '개발 중',
};

/**
 * @param {object} props
 * @param {import('../../data/products').Product} props.product
 * @param {number} [props.index]
 * @param {'default'|'wide'} [props.variant] - wide: 통합 앱(뚝딱)처럼 미니 도구를 함께 보여주는 가로형
 */
const ProductCard = ({ product, index = 0, variant = 'default' }) => {
  const reduced = useReducedMotion();
  const isDev = product.status !== 'live';
  const hasExternal = Boolean(product.externalUrl);
  const thumb = product.screenshots?.[0];
  const showThumb = Boolean(thumb && !isDev);
  const thumbSize = product.screenshotSizes?.[0] ?? { width: 540, height: 960 };
  const to = hasExternal ? product.externalUrl : `/products/${product.slug}`;

  const badges = (
    <div className="ft-pcard__badges">
      <span className="ft-pcard__chip">{getThemeLabel(product.theme)}</span>
      <span className={`ft-pcard__chip ft-pcard__chip--${isDev ? 'dev' : 'live'}`}>
        {STATUS_LABEL[product.status] ?? product.status}
      </span>
    </div>
  );

  const footer = (
    <div className="ft-pcard__footer">
      <span className="ft-pcard__more">
        {isDev ? '출시 알림 받기' : hasExternal ? '바로 가기' : '자세히 보기'}
        <ArrowUpRight size={16} strokeWidth={2.5} />
      </span>
      {product.storeUrl && (
        <a
          className="ft-pcard__store"
          href={product.storeUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Play
        </a>
      )}
    </div>
  );

  return (
    <motion.article
      className={[
        'ft-pcard',
        `ft-pcard--${variant}`,
        showThumb ? 'ft-pcard--visual' : '',
        isDev ? 'ft-pcard--dev' : '',
      ].filter(Boolean).join(' ')}
      style={{ '--product-color': product.color }}
      initial={reduced ? false : { opacity: 0, y: 24 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: (index % 6) * 0.06, ease: [0.16, 1, 0.3, 1] }}
    >
      {showThumb && (
        <div className="ft-pcard__thumb">
          <img
            src={thumb}
            alt={`${product.nameKo} 서비스 화면`}
            width={thumbSize.width}
            height={thumbSize.height}
            style={{ aspectRatio: `${thumbSize.width} / ${thumbSize.height}` }}
            loading="lazy"
          />
          {product.appIcon && (
            <img
              className="ft-pcard__thumb-icon"
              src={product.appIcon}
              alt=""
              width="256"
              height="256"
              loading="lazy"
            />
          )}
        </div>
      )}

      <div className="ft-pcard__body">
        {badges}
        <div className="ft-pcard__titles">
          <h3 className="ft-pcard__name-ko">{product.nameKo}</h3>
          <span className="ft-pcard__name-en">{product.name}</span>
        </div>
        <p className="ft-pcard__tagline">{product.tagline}</p>
        <p className="ft-pcard__desc">{product.description}</p>
        {variant !== 'wide' && footer}
      </div>

      {variant === 'wide' && product.subApps && (
        <ul className="ft-pcard__subapps">
          {product.subApps.map((s) => (
            <li key={s.name} className="ft-pcard__subapp">
              <strong>{s.name}</strong>
              <span>{s.desc}</span>
            </li>
          ))}
        </ul>
      )}

      {variant === 'wide' && <div className="ft-pcard__wide-footer">{footer}</div>}

      {/* 카드 전체를 덮는 링크. 스토어 버튼은 이 위에 온다. */}
      {isDev ? (
        <Link
          to="/contact"
          className="ft-pcard__overlay"
          aria-label={`${product.nameKo} 출시 알림 문의하기`}
        />
      ) : hasExternal ? (
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className="ft-pcard__overlay"
          aria-label={`${product.nameKo} 새 창에서 열기`}
        />
      ) : (
        <Link
          to={to}
          className="ft-pcard__overlay"
          aria-label={`${product.nameKo} 자세히 보기`}
        />
      )}
    </motion.article>
  );
};

export default ProductCard;
